import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../config/api';
import ActivityModal from './ActivityModal';
import ConfirmationDialog from './ConfirmationDialog';
import ItemDetailModal from './ItemDetailModal';
import ItineraryReservations from './ItineraryReservations';
import ItineraryStop from './ItineraryStop';
import ItineraryDragDropProvider, { ItineraryDayDragArea } from './ItineraryDayDragDrop';
import DocumentSectionHead from './DocumentSectionHead';
import useConfirmation from '../hooks/useConfirmation';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Collapse,
  TextField,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Flight as FlightIcon,
  Hotel as HotelIcon,
  Restaurant as RestaurantIcon,
  DirectionsCar as CarIcon,
  Event as EventIcon,
  LocalActivity as ActivityIcon,
} from '@mui/icons-material';
import { addDays, differenceInDays, format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import {
  DOCUMENT,
  documentOutlineButtonSx,
  documentSubTabsSx,
  documentTabIndicatorSx,
  documentTextButtonSx,
} from '../constants/documentTheme';
import { canEditTripItinerary } from '../utils/tripUtils';
import { getParticipantKey } from '../utils/memberItineraryParticipants';
import { isItineraryItemOnDay } from '../utils/itineraryDaySpan';
import PartnerPersonalAddOnButton from './PartnerPersonalAddOnButton';
import { isGroupTrip } from '../utils/tripUtils';
import {
  formatTime24,
  getDayTimeSpan,
  isReservationOnlyItem,
} from '../utils/itineraryDocumentUtils';

const normalizeMemberItem = (item) => {
  if (!item) return item;
  let transportationData = item.transportation_data;
  if (typeof transportationData === 'string') {
    try {
      transportationData = JSON.parse(transportationData);
    } catch {
      transportationData = {};
    }
  }
  return {
    ...item,
    transportation_data: transportationData || {},
  };
};

const MyItinerary = ({ trip, isCreator, isPartner = false, onAddActivity, onCreateSuggestion }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canMutate = canEditTripItinerary(trip);
  const authConfig = useMemo(
    () => (isPartner ? { usePartnerAuth: true } : {}),
    [isPartner]
  );

  const [items, setItems] = useState([]);
  const { confirmationState, showConfirmation, hideConfirmation } = useConfirmation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [expandedDays, setExpandedDays] = useState({});
  const [expandedStops, setExpandedStops] = useState({});

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [dialogMode, setDialogMode] = useState('create');
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [personalOwners, setPersonalOwners] = useState([]);
  const [canManagePersonalForMembers, setCanManagePersonalForMembers] = useState(false);
  const [selectedPersonalOwnerId, setSelectedPersonalOwnerId] = useState(null);

  const getCategoryColor = (category) => {
    const colors = {
      activity: '#10b981',
      restaurant: '#f59e0b',
      hotel: '#8b5cf6',
      transport: '#3b82f6',
      event: '#ec4899',
      other: '#6b7280',
    };
    return colors[category] || colors.other;
  };

  const getCategoryIcon = (category, transportationType = null) => {
    if (category === 'transport') {
      const transportIcons = {
        flight: <FlightIcon sx={{ fontSize: 20 }} />,
        car_rental: <CarIcon sx={{ fontSize: 20 }} />,
        other: <CarIcon sx={{ fontSize: 20 }} />,
      };
      return transportIcons[transportationType] || <CarIcon sx={{ fontSize: 20 }} />;
    }

    const icons = {
      activity: <ActivityIcon sx={{ fontSize: 20 }} />,
      restaurant: <RestaurantIcon sx={{ fontSize: 20 }} />,
      hotel: <HotelIcon sx={{ fontSize: 20 }} />,
      event: <EventIcon sx={{ fontSize: 20 }} />,
      other: <EventIcon sx={{ fontSize: 20 }} />,
    };
    return icons[category] || icons.other;
  };

  const getCurrencySymbol = (currency) => {
    const symbols = {
      USD: '$',
      EUR: '€',
      GBP: '£',
      JPY: '¥',
      CAD: 'C$',
      AUD: 'A$',
    };
    return symbols[currency] || '$';
  };

  const generateGoogleMapsLink = (location) => (
    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`
  );

  const [formData, setFormData] = useState({
    name: '',
    location: '',
    google_map_link: '',
    category: 'activity',
    start_datetime: '',
    end_datetime: '',
    description: '',
    visibility: 'personal',
    subgroup_name: '',
    participants: [],
    owner_user_id: null,
  });

  const [tripMembers, setTripMembers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [reordering, setReordering] = useState(false);

  const tabs = [
    { label: t('itinerary.personalTab', { defaultValue: 'Personal' }), value: 'personal', icon: <PersonIcon /> },
    { label: t('itinerary.subGroupsTab', { defaultValue: 'Sub-Groups' }), value: 'subgroup', icon: <GroupIcon /> },
  ];

  const fetchItems = useCallback(async () => {
    if (!trip?.id) return;

    try {
      setLoading(true);
      const response = await api.get(`/member-itinerary/trip/${trip.id}`, authConfig);
      const next = (response.data?.items || []).map(normalizeMemberItem);
      setItems(next);
      if (response.data?.canManagePersonalForMembers != null) {
        setCanManagePersonalForMembers(Boolean(response.data.canManagePersonalForMembers));
      }
    } catch (err) {
      console.error('Error fetching member itinerary:', err);
      setError('Failed to load member itinerary');
    } finally {
      setLoading(false);
    }
  }, [trip?.id, authConfig]);

  const fetchTripMembers = useCallback(async () => {
    if (!trip?.id) return;

    try {
      const response = await api.get(`/member-itinerary/trip/${trip.id}/members`, authConfig);
      setTripMembers(response.data?.members || []);
      setPersonalOwners(response.data?.personalOwners || []);
      if (response.data?.canManagePersonalForMembers != null) {
        setCanManagePersonalForMembers(Boolean(response.data.canManagePersonalForMembers));
      }
    } catch (err) {
      console.error('Error fetching trip members:', err);
    }
  }, [trip?.id, authConfig]);

  useEffect(() => {
    fetchItems();
    fetchTripMembers();
  }, [fetchItems, fetchTripMembers]);

  const assignablePersonalMembers = useMemo(() => {
    const byId = new Map();
    tripMembers.forEach((member) => {
      const userId = Number(member.user_id);
      if (!Number.isInteger(userId) || userId <= 0) return;
      byId.set(userId, {
        user_id: userId,
        username: member.username || member.email || `User ${userId}`,
        email: member.email || '',
        item_count: 0,
        traveler_trip_id: member.traveler_trip_id ? Number(member.traveler_trip_id) : null,
      });
    });
    personalOwners.forEach((owner) => {
      const userId = Number(owner.user_id);
      if (!Number.isInteger(userId) || userId <= 0) return;
      const prev = byId.get(userId);
      byId.set(userId, {
        user_id: userId,
        username: owner.username || prev?.username || owner.email || `User ${userId}`,
        email: owner.email || prev?.email || '',
        item_count: 0,
        traveler_trip_id: prev?.traveler_trip_id || null,
      });
    });
    items.forEach((item) => {
      if (item.visibility !== 'personal') return;
      const userId = Number(item.owner_user_id || item.created_by);
      if (!Number.isInteger(userId) || userId <= 0) return;
      const prev = byId.get(userId) || {
        user_id: userId,
        username: item.creator_name || item.creator_email || `User ${userId}`,
        email: item.creator_email || '',
        item_count: 0,
      };
      byId.set(userId, { ...prev, item_count: (prev.item_count || 0) + 1 });
    });
    return [...byId.values()].sort((a, b) => String(a.username).localeCompare(String(b.username)));
  }, [tripMembers, personalOwners, items]);

  // Dropdown lists members who already have personal items; if none yet, show
  // every assignable member so organizers can start a first personal plan.
  const personalMemberOptions = useMemo(() => {
    const withItems = assignablePersonalMembers.filter((member) => member.item_count > 0);
    return withItems.length > 0 ? withItems : assignablePersonalMembers;
  }, [assignablePersonalMembers]);

  const showPersonalMemberSelect = canManagePersonalForMembers && personalMemberOptions.length > 0;

  useEffect(() => {
    if (!showPersonalMemberSelect) return;
    setSelectedPersonalOwnerId((current) => {
      if (current && personalMemberOptions.some((member) => member.user_id === current)) {
        return current;
      }
      return personalMemberOptions[0]?.user_id ?? null;
    });
  }, [showPersonalMemberSelect, personalMemberOptions]);

  const filteredItems = useMemo(() => {
    const byVisibility = items.filter((item) => (
      activeTab === 0 ? item.visibility === 'personal' : item.visibility === 'subgroup'
    ));
    if (activeTab !== 0 || !showPersonalMemberSelect || !selectedPersonalOwnerId) {
      return byVisibility;
    }
    return byVisibility.filter((item) => (
      Number(item.owner_user_id || item.created_by) === Number(selectedPersonalOwnerId)
    ));
  }, [items, activeTab, showPersonalMemberSelect, selectedPersonalOwnerId]);

  const selectedPersonalOwner = useMemo(
    () => personalMemberOptions.find((member) => member.user_id === selectedPersonalOwnerId) || null,
    [personalMemberOptions, selectedPersonalOwnerId]
  );

  // Match main itinerary: reservations strip reads flights/hotels/cars from the
  // full list; the day spine only drops reservation-only bookings.
  const dayItems = useMemo(
    () => filteredItems.filter((item) => !isReservationOnlyItem(item)),
    [filteredItems]
  );

  const itineraryByDays = useMemo(() => {
    if (!trip?.start_date || !trip?.end_date) {
      // Fall back to days that have items when trip dates are missing.
      const byDate = new Map();
      dayItems.forEach((item) => {
        if (!item.start_datetime) return;
        const key = String(item.start_datetime).slice(0, 10);
        if (!byDate.has(key)) byDate.set(key, []);
        byDate.get(key).push(item);
      });
      return [...byDate.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, list], index) => ({
          dayNumber: index + 1,
          date: new Date(`${key}T00:00:00`),
          items: list.sort((a, b) => {
            const timeA = a.start_datetime ? new Date(a.start_datetime).getTime() : 0;
            const timeB = b.start_datetime ? new Date(b.start_datetime).getTime() : 0;
            return timeA - timeB;
          }),
        }));
    }

    const tripStartDate = new Date(`${String(trip.start_date).split('T')[0]}T00:00:00`);
    const tripEndDate = new Date(`${String(trip.end_date).split('T')[0]}T23:45:59`);
    const totalDays = differenceInDays(tripEndDate, tripStartDate) + 1;
    const days = [];

    for (let i = 0; i < totalDays; i += 1) {
      const currentDate = addDays(tripStartDate, i);
      const itemsForDay = dayItems
        .filter((item) => isItineraryItemOnDay(item, currentDate))
        .sort((a, b) => {
          const timeA = a.start_datetime ? new Date(a.start_datetime).getTime() : 0;
          const timeB = b.start_datetime ? new Date(b.start_datetime).getTime() : 0;
          if (timeA === timeB) return 0;
          if (timeA === 0) return 1;
          if (timeB === 0) return -1;
          return timeA - timeB;
        });
      days.push({
        dayNumber: i + 1,
        date: currentDate,
        items: itemsForDay,
      });
    }
    return days;
  }, [trip, dayItems]);

  // Match the main itinerary: default every day to expanded. Empty days still
  // render as free-day rows (`isOpen` requires items); planned days stay open
  // even if they were first seen while items were still loading.
  useEffect(() => {
    setExpandedDays((current) => {
      const next = { ...current };
      let changed = false;
      itineraryByDays.forEach((day) => {
        if (next[day.dayNumber] === undefined) {
          next[day.dayNumber] = true;
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }, [itineraryByDays]);

  const plannedStopCount = useMemo(
    () => itineraryByDays.reduce((count, day) => count + day.items.length, 0),
    [itineraryByDays]
  );

  const canReorderPersonal = Boolean(
    canMutate
    && activeTab === 0
    && (
      isPartner
      || canManagePersonalForMembers
      || Number(selectedPersonalOwnerId || user?.id) === Number(user?.id)
    )
  );

  const canDragPersonalItem = useCallback((item) => {
    if (item?.visibility && item.visibility !== 'personal') return false;
    const ownerId = Number(item?.owner_user_id || item?.created_by);
    if (!Number.isInteger(ownerId) || ownerId <= 0) return false;
    if (isPartner || canManagePersonalForMembers) {
      return !selectedPersonalOwnerId || ownerId === Number(selectedPersonalOwnerId);
    }
    return ownerId === Number(user?.id);
  }, [isPartner, canManagePersonalForMembers, selectedPersonalOwnerId, user?.id]);

  const handlePersonalReorder = async (updates) => {
    setReordering(true);
    setError(null);
    try {
      await Promise.all(updates.map(async (update) => {
        const item = items.find((entry) => entry.id === update.id);
        if (!item) return null;
        return api.put(`/member-itinerary/${item.id}`, {
          name: item.name,
          location: item.location || '',
          google_map_link: item.google_map_link || '',
          category: item.category || 'activity',
          start_datetime: update.start_datetime ?? item.start_datetime,
          end_datetime: update.end_datetime ?? item.end_datetime,
          description: item.description || '',
          visibility: item.visibility || 'personal',
          subgroup_name: item.subgroup_name || '',
          participants: (item.participants || []).map(getParticipantKey).filter(Boolean),
          transportation_type: item.transportation_type || '',
          transportation_data: update.transportation_data ?? item.transportation_data ?? {},
        }, authConfig);
      }));
      await fetchItems();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      await fetchItems();
    } finally {
      setReordering(false);
    }
  };

  const renderPersonalStop = (item, isLast, currentDayDate) => {
    const day = itineraryByDays.find((entry) => entry.items.some((stop) => stop.id === item.id));
    const index = day ? day.items.findIndex((stop) => stop.id === item.id) : -1;
    const nextItem = index >= 0 ? day.items[index + 1] || null : null;
    return (
      <ItineraryStop
        item={item}
        isLast={isLast}
        nextItem={nextItem}
        currentDayDate={currentDayDate}
        expanded={Boolean(expandedStops[item.id])}
        onToggle={() => toggleStop(item.id)}
        canEdit={canMutate}
        onEdit={canMutate ? () => handleDialogOpen('edit', item) : undefined}
        onDelete={canMutate ? () => handleDelete(item.id) : undefined}
        onOpenMaps={item.location ? () => {
          window.open(generateGoogleMapsLink(item.location), '_blank', 'noopener,noreferrer');
        } : undefined}
      />
    );
  };

  const handleTabChange = (_event, newValue) => {
    setActiveTab(newValue);
  };

  const handleDialogOpen = (mode = 'create', item = null) => {
    if (!canMutate) return;
    setDialogMode(mode);
    setEditingItem(item);

    if (mode === 'create') {
      setFormData({
        name: '',
        location: '',
        google_map_link: '',
        category: 'activity',
        start_datetime: '',
        end_datetime: '',
        description: '',
        visibility: activeTab === 1 ? 'subgroup' : 'personal',
        subgroup_name: '',
        participants: [],
        owner_user_id: activeTab === 0 ? selectedPersonalOwnerId : null,
      });
    } else if (mode === 'edit' && item) {
      setFormData({
        name: item.name,
        location: item.location || '',
        google_map_link: item.google_map_link || '',
        category: item.category,
        start_datetime: item.start_datetime ? format(parseISO(item.start_datetime), "yyyy-MM-dd'T'HH:mm") : '',
        end_datetime: item.end_datetime ? format(parseISO(item.end_datetime), "yyyy-MM-dd'T'HH:mm") : '',
        description: item.description || '',
        visibility: item.visibility,
        subgroup_name: item.subgroup_name || '',
        participants: (item.participants || []).map(getParticipantKey).filter(Boolean),
        owner_user_id: item.owner_user_id || item.created_by || null,
      });
    }

    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingItem(null);
    setError(null);
    setFormData({
      name: '',
      location: '',
      google_map_link: '',
      category: 'activity',
      start_datetime: '',
      end_datetime: '',
      description: '',
      visibility: activeTab === 1 ? 'subgroup' : 'personal',
      subgroup_name: '',
      participants: [],
      owner_user_id: selectedPersonalOwnerId,
    });
  };

  const handleSubmit = async (submittedFormData) => {
    setSubmitting(true);
    setError(null);

    try {
      const payload = {
        tripId: trip.id,
        ...submittedFormData,
        participants: submittedFormData.visibility === 'subgroup' ? submittedFormData.participants : [],
        owner_user_id: submittedFormData.visibility === 'personal' && canManagePersonalForMembers
          ? (submittedFormData.owner_user_id || selectedPersonalOwnerId)
          : undefined,
      };

      const url = dialogMode === 'create'
        ? '/member-itinerary'
        : `/member-itinerary/${editingItem.id}`;

      const method = dialogMode === 'create' ? 'POST' : 'PUT';

      const response = await api({
        method,
        url,
        data: payload,
        ...authConfig,
      });

      if (dialogMode === 'create') {
        const created = normalizeMemberItem(response.data.item);
        setItems((prev) => [...prev, created]);
        if (created?.visibility === 'personal') {
          const ownerId = Number(created.owner_user_id || created.created_by);
          if (Number.isInteger(ownerId) && ownerId > 0) {
            setSelectedPersonalOwnerId(ownerId);
          }
        }
        fetchTripMembers();
      } else {
        await fetchItems();
      }

      handleDialogClose();
    } catch (err) {
      console.error('Error saving item:', err);
      setError(err.response?.data?.error || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (itemId) => {
    if (!canMutate) return;
    showConfirmation({
      title: 'Delete Item',
      message: 'Are you sure you want to delete this item? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      severity: 'error',
      onConfirm: async () => {
        try {
          await api.delete(`/member-itinerary/${itemId}`, authConfig);
          setItems((prev) => prev.filter((item) => item.id !== itemId));
        } catch (err) {
          console.error('Error deleting item:', err);
          setError('Failed to delete item');
        }
      },
    });
  };

  const handleMoveToShared = (item) => {
    if (!canMutate || !item) return;
    showConfirmation({
      title: t('itinerary.detailModal.moveToShared'),
      message: t('itinerary.detailModal.moveToSharedConfirm', {
        defaultValue: 'Move this personal activity onto the shared group itinerary?',
      }),
      confirmText: t('itinerary.detailModal.moveToShared'),
      cancelText: t('common.cancel', { defaultValue: 'Cancel' }),
      onConfirm: async () => {
        try {
          await api.post(`/member-itinerary/${item.id}/move-to-shared`, {}, authConfig);
          await fetchItems();
          setDetailModalOpen(false);
        } catch (err) {
          console.error('Error moving item to shared:', err);
          setError(err.response?.data?.error || 'Failed to move item to shared itinerary');
        }
      },
    });
  };

  const findItem = (itemId) => filteredItems.find((entry) => String(entry.id) === String(itemId));

  const openItemDetails = (itemId) => {
    const item = findItem(itemId);
    if (!item) return;
    setSelectedItem(item);
    setDetailModalOpen(true);
  };

  const toggleDay = (dayNumber) => {
    setExpandedDays((current) => ({
      ...current,
      [dayNumber]: !current[dayNumber],
    }));
  };

  const toggleStop = (itemId) => {
    setExpandedStops((current) => ({
      ...current,
      [itemId]: !current[itemId],
    }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box data-testid="my-itinerary-panel" sx={{ p: { xs: 0, md: 0.5 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5, gap: 2, flexWrap: 'wrap' }}>
        <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: { xs: 22, md: 26 }, fontWeight: 400 }}>
          {isPartner ? t('itinerary.tabs.clientItinerary') : t('itinerary.tabs.personalItinerary')}
        </Typography>
        {canMutate ? (
          <Button
            startIcon={<AddIcon />}
            onClick={() => handleDialogOpen('create')}
            sx={documentOutlineButtonSx}
          >
            {t('itinerary.addActivity')}
          </Button>
        ) : null}
      </Box>

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        textColor="inherit"
        TabIndicatorProps={{ sx: documentTabIndicatorSx }}
        sx={{ ...documentSubTabsSx, mb: showPersonalMemberSelect && activeTab === 0 ? 1.5 : 3 }}
      >
        {tabs.map((tab, index) => (
          <Tab
            key={tab.value}
            value={index}
            label={tab.label}
            icon={tab.icon}
            iconPosition="start"
          />
        ))}
      </Tabs>

      {showPersonalMemberSelect && activeTab === 0 ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
            flexDirection: { xs: 'column', sm: 'row' },
            mb: 3,
          }}
        >
          <TextField
            select
            size="small"
            data-testid="personal-member-select"
            label={t('itinerary.personalMemberLabel', { defaultValue: 'Member itinerary' })}
            value={selectedPersonalOwnerId || ''}
            onChange={(event) => setSelectedPersonalOwnerId(Number(event.target.value))}
            sx={{
              minWidth: { xs: '100%', sm: 260 },
              '& .MuiInputBase-root': { bgcolor: DOCUMENT.surface },
            }}
          >
            {personalMemberOptions.map((member) => (
              <MenuItem key={member.user_id} value={member.user_id}>
                {member.username}
                {member.item_count > 0
                  ? ` (${t('itinerary.personalMemberItemCount', {
                    count: member.item_count,
                    defaultValue: '{{count}} items',
                  })})`
                  : ''}
              </MenuItem>
            ))}
          </TextField>
          {selectedPersonalOwner ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'stretch', sm: 'flex-end' }, gap: 1 }}>
              <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3 }}>
                {t('itinerary.personalMemberHint', {
                  name: selectedPersonalOwner.username,
                  defaultValue: 'Showing personal plans for {{name}} — flights, hotels, and day stops from their city.',
                })}
              </Typography>
              {isPartner && isGroupTrip(trip) && selectedPersonalOwner.traveler_trip_id ? (
                <PartnerPersonalAddOnButton
                  travelerTripId={selectedPersonalOwner.traveler_trip_id}
                  travelerName={selectedPersonalOwner.username}
                />
              ) : null}
            </Box>
          ) : null}
        </Box>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      {filteredItems.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 6,
            px: 2,
            border: `1px dashed ${DOCUMENT.ruleHard}`,
            bgcolor: DOCUMENT.canvas,
          }}
        >
          <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 22, color: DOCUMENT.ink2, mb: 1 }}>
            {activeTab === 0
              ? (selectedPersonalOwner
                ? t('itinerary.emptyPersonalForMemberTitle', {
                  name: selectedPersonalOwner.username,
                  defaultValue: 'No personal activities for {{name}} yet',
                })
                : t('itinerary.emptyPersonalTitle', { defaultValue: 'No personal activities yet' }))
              : t('itinerary.emptySubgroupTitle', { defaultValue: 'No sub-group activities yet' })}
          </Typography>
          <Typography sx={{ color: DOCUMENT.ink3, fontSize: 14, mb: 2.5 }}>
            {activeTab === 0
              ? t('itinerary.emptyPersonalBody', {
                defaultValue: 'Add personal flights, hotels, and day plans for this traveler — useful when people join from different cities.',
              })
              : t('itinerary.emptySubgroupBody', { defaultValue: 'Create sub-group activities with selected members.' })}
          </Typography>
          {canMutate ? (
            <Button sx={documentOutlineButtonSx} startIcon={<AddIcon />} onClick={() => handleDialogOpen('create')}>
              {t('itinerary.addActivity')}
            </Button>
          ) : null}
        </Box>
      ) : (
        <>
          <ItineraryReservations
            items={filteredItems}
            onSelectItem={openItemDetails}
            onEditItem={canMutate ? (itemId) => {
              const item = findItem(itemId);
              if (item) handleDialogOpen('edit', item);
            } : undefined}
            onRemoveItem={canMutate ? handleDelete : undefined}
            canEditItem={() => canMutate}
          />

          <Box sx={{ px: { xs: 0, md: 1 }, py: { xs: 1, md: 2 } }}>
            <DocumentSectionHead
              sx={{ alignItems: 'center' }}
              title={t('itinerary.dayByDay')}
              meta={t('itinerary.dayByDayMeta', {
                days: itineraryByDays.length,
                planned: plannedStopCount,
              })}
            />

            <ItineraryDragDropProvider
              days={itineraryByDays}
              disabled={!canReorderPersonal}
              isSaving={reordering}
              renderItem={renderPersonalStop}
              renderRoadTripCard={() => null}
              onReorder={handlePersonalReorder}
              tripStartDate={String(trip?.start_date || '').split('T')[0] || null}
              canDragItem={canDragPersonalItem}
            >
            {itineraryByDays.map((day) => {
              const hasItems = day.items.length > 0;
              const isExpanded = expandedDays[day.dayNumber] !== false;
              const isOpen = hasItems && isExpanded;
              const dayLabel = format(day.date, 'EEEE, d MMMM');
              const timeSpan = getDayTimeSpan(day.items, formatTime24);
              const dayCardSx = {
                mt: 1.5,
                bgcolor: isOpen ? DOCUMENT.surface : DOCUMENT.canvas,
                border: isOpen ? `1px solid ${DOCUMENT.ruleHard}` : `1px dashed ${DOCUMENT.ruleHard}`,
              };

              if (!hasItems) {
                return (
                  <Box
                    key={day.dayNumber}
                    data-testid={`my-itinerary-empty-day-${day.dayNumber}`}
                    sx={dayCardSx}
                  >
                    <ItineraryDayDragArea
                      day={day}
                      renderItem={renderPersonalStop}
                      renderRoadTripCard={() => null}
                      renderAddActivitySection={() => null}
                      renderEmptyDaySection={() => (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'baseline',
                            gap: 2.25,
                            px: 2.25,
                            py: 1.75,
                            flexWrap: 'wrap',
                          }}
                        >
                          <Typography sx={{ fontSize: 10, letterSpacing: '0.19em', color: DOCUMENT.ink3, width: 54, flexShrink: 0 }}>
                            {t('itinerary.dayNumber', { number: String(day.dayNumber).padStart(2, '0') })}
                          </Typography>
                          <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: { xs: 16, sm: 18 }, fontWeight: 400, color: DOCUMENT.ink }}>
                            {dayLabel}
                          </Typography>
                          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'baseline', gap: 2, flexWrap: 'wrap' }}>
                            <Typography sx={{ fontSize: 11.5, color: DOCUMENT.ink3 }}>
                              {t('itinerary.status.freeDay', { defaultValue: 'Free day' })}
                            </Typography>
                            {canMutate ? (
                              <Button sx={documentTextButtonSx} onClick={() => handleDialogOpen('create')}>
                                {t('itinerary.addActivity')}
                              </Button>
                            ) : null}
                          </Box>
                        </Box>
                      )}
                    />
                  </Box>
                );
              }

              return (
                <Box
                  key={day.dayNumber}
                  data-testid={`my-itinerary-day-${day.dayNumber}`}
                  sx={dayCardSx}
                >
                  <Box
                    onClick={() => toggleDay(day.dayNumber)}
                    sx={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 1.5,
                      px: 1.75,
                      pt: 1.25,
                      pb: 1,
                      borderBottom: `1px solid ${DOCUMENT.rule}`,
                      cursor: 'pointer',
                    }}
                  >
                    <Typography sx={{ fontSize: 10, letterSpacing: '0.16em', color: DOCUMENT.accent, width: 48, flexShrink: 0 }}>
                      {t('itinerary.dayNumber', { number: String(day.dayNumber).padStart(2, '0') })}
                    </Typography>
                    <Typography
                      component="h3"
                      sx={{
                        fontFamily: DOCUMENT.sans,
                        fontSize: { xs: 13, sm: 14 },
                        fontWeight: 600,
                        letterSpacing: '0.04em',
                        textTransform: 'uppercase',
                        lineHeight: 1.3,
                        color: DOCUMENT.ink,
                      }}
                    >
                      {dayLabel}
                    </Typography>
                    <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <Typography sx={{ fontSize: 11.5, color: DOCUMENT.ink3 }}>
                        {t('itinerary.activity', { count: day.items.length })}
                      </Typography>
                      {timeSpan ? (
                        <Typography sx={{ fontSize: 11.5, color: DOCUMENT.ink3, fontVariantNumeric: 'tabular-nums' }}>
                          {timeSpan}
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>

                  <Collapse in={isOpen} timeout="auto" unmountOnExit={false}>
                    <Box sx={{ px: { xs: 1.25, sm: 1.75 }, pt: 1.5, pb: 1 }}>
                      <ItineraryDayDragArea
                        day={day}
                        renderItem={renderPersonalStop}
                        renderRoadTripCard={() => null}
                        renderAddActivitySection={() => (
                          canMutate ? (
                            <Box sx={{ display: 'flex', gap: 2, py: 1.5 }}>
                              <Button sx={documentTextButtonSx} onClick={() => handleDialogOpen('create')}>
                                {t('itinerary.addActivity')}
                              </Button>
                            </Box>
                          ) : null
                        )}
                        renderEmptyDaySection={() => null}
                      />
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
            </ItineraryDragDropProvider>
          </Box>
        </>
      )}

      <ActivityModal
        open={dialogOpen}
        onClose={handleDialogClose}
        onSubmit={handleSubmit}
        mode={dialogMode}
        initialData={formData}
        tripMembers={tripMembers}
        // Owner comes from the page "Member itinerary" dropdown — no second picker.
        personalOwnerOptions={[]}
        canAssignPersonalOwner={false}
        submitting={submitting}
        submitError={error}
        onClearSubmitError={() => setError(null)}
        showVisibility={true}
        showVisibilitySelect={false}
        defaultVisibility="personal"
        trip={trip}
        usePartnerAuth={isPartner}
      />

      <ConfirmationDialog
        open={confirmationState.open}
        onClose={hideConfirmation}
        onConfirm={confirmationState.onConfirm}
        title={confirmationState.title}
        message={confirmationState.message}
        confirmText={confirmationState.confirmText}
        cancelText={confirmationState.cancelText}
        severity={confirmationState.severity}
        icon={confirmationState.icon}
      />

      <ItemDetailModal
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        item={selectedItem}
        canEdit={canMutate}
        onEdit={canMutate ? () => handleDialogOpen('edit', selectedItem) : undefined}
        onMoveToShared={
          canMutate && selectedItem?.visibility === 'personal'
            ? () => handleMoveToShared(selectedItem)
            : undefined
        }
        getCategoryColor={getCategoryColor}
        getCategoryIcon={getCategoryIcon}
        getCurrencySymbol={getCurrencySymbol}
        generateGoogleMapsLink={generateGoogleMapsLink}
      />
    </Box>
  );
};

export default MyItinerary;
